import React from 'react'
import { motion } from 'framer-motion'
import { Shield, Calendar, Mail, Phone, Lock, Eye, Database, UserCheck } from 'lucide-react'
import SEO from '@components/SEO'

const PrivacyPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' }
  }

  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })

  return (
    <div className="pt-14">
      <SEO 
        title="Privacy Policy - Total Rewards Pro"
        description="Privacy Policy for Total Rewards Pro - Learn how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, personal information, total rewards pro, compensation consulting"
      />
      
      {/* Hero Section */}
      <section className="pt-14 lg:pt-16 pb-20 lg:pb-32 text-white relative overflow-hidden">
        {/* Background Image with WebP fallback */}
        <picture className="absolute inset-0">
          <source srcSet="/privacy-hero.webp" type="image/webp" />
          <img 
            src="/privacy-hero.jpg" 
            alt="Modern corporate office space"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
        </picture>
        
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/80"></div>
        
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 pattern-dots opacity-10"></div>
        
        <div className="max-w-4xl mx-auto w-full px-6 sm:px-8 lg:px-12 text-center relative">
          <motion.div {...fadeInUp}>
            <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-professional">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-display-2 text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-body text-neutral-200 mb-4">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
            <div className="flex items-center justify-center space-x-2 text-sm text-muted">
              <Calendar className="w-4 h-4" />
              <span>Last updated: {currentDate}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto w-full px-6 sm:px-8 lg:px-12">
          <motion.div 
            className="prose prose-lg max-w-none"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="space-y-8">
              {/* Introduction */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">1. Introduction</h2>
                <p className="text-body text-muted mb-4">
                  iTotal Rewards Pro LLP ("we," "our," or "us") operates the https://total-rewards-pro.web.app website (the "Service"). This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service and the choices you have associated with that data.
                </p>
                <p className="text-body text-muted">
                  We use your data to provide and improve the Service. By using the Service, you agree to the collection and use of information in accordance with this policy.
                </p>
              </div>

              {/* Information We Collect */}
              <div className="card p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <Database className="w-6 h-6 text-primary-600" />
                  <h2 className="text-heading-1 text-navy-900">2. Information We Collect</h2>
                </div>
                
                <h3 className="text-heading-2 text-navy-900 mb-3">Personal Information</h3>
                <p className="text-body text-muted mb-4">
                  While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you. This may include:
                </p>
                <ul className="list-disc list-inside text-body text-muted space-y-2 mb-6">
                  <li>First name and last name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Company name and position</li>
                  <li>Address, State, Province, ZIP/Postal code, City</li>
                </ul>

                <h3 className="text-heading-2 text-navy-900 mb-3">Usage Data</h3>
                <p className="text-body text-muted mb-4">
                  We may also collect information on how the Service is accessed and used ("Usage Data"). This Usage Data may include information such as your computer's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
                </p>
              </div>

              {/* How We Use Information */}
              <div className="card p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <UserCheck className="w-6 h-6 text-primary-600" />
                  <h2 className="text-heading-1 text-navy-900">3. How We Use Your Information</h2>
                </div>
                <p className="text-body text-muted mb-4">
                  Total Rewards Pro uses the collected data for various purposes:
                </p>
                <ul className="list-disc list-inside text-body text-muted space-y-2">
                  <li>To provide and maintain our Service</li>
                  <li>To notify you about changes to our Service</li>
                  <li>To allow you to participate in interactive features of our Service when you choose to do so</li>
                  <li>To provide customer support</li>
                  <li>To gather analysis or valuable information so that we can improve our Service</li>
                  <li>To monitor the usage of our Service</li>
                  <li>To detect, prevent and address technical issues</li>
                  <li>To provide you with news, special offers and general information about other goods, services and events which we offer</li>
                </ul>
              </div>

              {/* Legal Basis */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">4. Legal Basis for Processing Personal Data</h2>
                <p className="text-body text-muted mb-4">
                  If you are from the European Economic Area (EEA), Total Rewards Pro's legal basis for collecting and using the personal information described in this Privacy Policy depends on the Personal Data we collect and the specific context in which we collect it.
                </p>
                <p className="text-body text-muted">
                  Total Rewards Pro may process your Personal Data because:
                </p>
                <ul className="list-disc list-inside text-body text-muted space-y-2 mt-4">
                  <li>We need to perform a contract with you</li>
                  <li>You have given us permission to do so</li>
                  <li>The processing is in our legitimate interests and it's not overridden by your rights</li>
                  <li>For payment processing purposes</li>
                  <li>To comply with the law</li>
                </ul>
              </div>

              {/* Data Retention */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">5. Data Retention</h2>
                <p className="text-body text-muted">
                  Total Rewards Pro will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your Personal Data to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our legal agreements and policies.
                </p>
              </div>

              {/* Data Transfer */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">6. Transfer of Data</h2>
                <p className="text-body text-muted mb-4">
                  Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from your jurisdiction.
                </p>
                <p className="text-body text-muted">
                  Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
                </p>
              </div>

              {/* Data Security */}
              <div className="card p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <Lock className="w-6 h-6 text-primary-600" />
                  <h2 className="text-heading-1 text-navy-900">7. Data Security</h2>
                </div>
                <p className="text-body text-muted mb-4">
                  The security of your data is important to us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.
                </p>
                <p className="text-body text-muted">
                  We implement appropriate security measures to protect against unauthorized access, alteration, disclosure, or destruction of your personal information, username, password, transaction information and data stored on our site.
                </p>
              </div>

              {/* Your Rights */}
              <div className="card p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <Eye className="w-6 h-6 text-primary-600" />
                  <h2 className="text-heading-1 text-navy-900">8. Your Data Protection Rights</h2>
                </div>
                <p className="text-body text-muted mb-4">
                  Total Rewards Pro aims to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Data. You have the following data protection rights:
                </p>
                <ul className="list-disc list-inside text-body text-muted space-y-2">
                  <li><strong>The right to access</strong> – You have the right to request copies of your personal data</li>
                  <li><strong>The right to rectification</strong> – You have the right to request that we correct any information you believe is inaccurate</li>
                  <li><strong>The right to erasure</strong> – You have the right to request that we erase your personal data, under certain conditions</li>
                  <li><strong>The right to restrict processing</strong> – You have the right to request that we restrict the processing of your personal data</li>
                  <li><strong>The right to object to processing</strong> – You have the right to object to our processing of your personal data</li>
                  <li><strong>The right to data portability</strong> – You have the right to request that we transfer the data that we have collected to another organization</li>
                </ul>
              </div>

              {/* Cookies */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">9. Cookies</h2>
                <p className="text-body text-muted mb-4">
                  We use cookies and similar tracking technologies to track the activity on our Service and hold certain information. Cookies are files with small amount of data which may include an anonymous unique identifier.
                </p>
                <p className="text-body text-muted mb-4">
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
                </p>
                <p className="text-body text-muted">
                  Examples of Cookies we use:
                </p>
                <ul className="list-disc list-inside text-body text-muted space-y-2 mt-4">
                  <li><strong>Session Cookies:</strong> We use Session Cookies to operate our Service</li>
                  <li><strong>Preference Cookies:</strong> We use Preference Cookies to remember your preferences and various settings</li>
                  <li><strong>Security Cookies:</strong> We use Security Cookies for security purposes</li>
                </ul>
              </div>

              {/* Third Party Services */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">10. Third-Party Service Providers</h2>
                <p className="text-body text-muted mb-4">
                  We may employ third party companies and individuals to facilitate our Service ("Service Providers"), to provide the Service on our behalf, to perform Service-related services or to assist us in analyzing how our Service is used.
                </p>
                <p className="text-body text-muted">
                  These third parties have access to your Personal Data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
                </p>
              </div>

              {/* Children's Privacy */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">11. Children's Privacy</h2>
                <p className="text-body text-muted">
                  Our Service does not address anyone under the age of 18 ("Children"). We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware that your child has provided us with Personal Data, please contact us.
                </p>
              </div>

              {/* Changes to Privacy Policy */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">12. Changes to This Privacy Policy</h2>
                <p className="text-body text-muted">
                  We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "last updated" date at the top of this Privacy Policy.
                </p>
              </div>

              {/* Contact Information */}
                              <div className="card p-8 bg-primary-50 border-primary-200">
                <h2 className="text-heading-1 text-navy-900 mb-4">13. Contact Us</h2>
                <p className="text-body text-muted mb-6">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-primary-600" />
                    <a href="mailto:info@totalrewardspro.com" className="text-primary-600 hover:text-primary-700 font-medium">
                      info@totalrewardspro.com
                    </a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-primary-600" />
                    <a href="tel:+919873655887" className="text-primary-600 hover:text-primary-700 font-medium">
                      +91-9873655887
                    </a>
                  </div>
                  <div className="text-body text-muted">
                    <strong>Address:</strong><br />
                    629, Khari Baoli Main Road<br />
                    Chandni Chowk<br />
                    Delhi – 110006<br />
                    India
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default PrivacyPage 